import {
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';

import {
  utils as commonUtils,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { FinancialData } from '@finley-technologies/finley-types';

import { CreateFinStatementLineItemInputGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { Select } from 'packages/common-frontend/components/Select';

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
  onDeleteFinStatement: () => Promise<void>;
  lineItemData: CreateFinStatementLineItemInputGQLType;
  setLineItemData: React.Dispatch<
    React.SetStateAction<CreateFinStatementLineItemInputGQLType | null>
  >;
  financialStatement: FinancialData.FinancialStatementType;
};

const generateSectionOptions = (
  financialStatement: FinancialData.FinancialStatementType,
): {
  value: CreateFinStatementLineItemInputGQLType['section'];
  label: string;
}[] => {
  switch (financialStatement) {
    case 'balanceSheet':
      return [
        { value: 'assets', label: commonUtils.toTitleCase('assets') },
        { value: 'liabilities', label: commonUtils.toTitleCase('liabilities') },
        { value: 'equity', label: commonUtils.toTitleCase('equity') },
      ];
    case 'incomeStatement':
      return [
        { value: 'income', label: commonUtils.toTitleCase('income') },
        { value: 'expenses', label: commonUtils.toTitleCase('expenses') },
      ];
    default:
      return finleyAssertUnreachable(financialStatement);
  }
};

export const CreateFinStatementLineItemModal = ({
  isOpen,
  onConfirm,
  onCancel,
  onDeleteFinStatement,
  lineItemData,
  setLineItemData,
  financialStatement,
}: Props) => {
  const sectionOptions = useMemo(
    () => generateSectionOptions(financialStatement),
    [financialStatement],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onCancel();
      }}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent p="5">
        <ModalHeader>Edit Finanical Statement</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Box>
            <Flex alignItems="center" mb="6">
              <Text mr="6px" minWidth="120px">
                Section:
              </Text>
              <Select
                value={lineItemData.section}
                onChange={(val) => {
                  if (val) {
                    setLineItemData((state) => {
                      if (!state) return state;
                      return {
                        ...state,
                        section: val,
                      };
                    });
                  }
                }}
                options={sectionOptions}
                width="300px"
              />
            </Flex>
            <Flex alignItems="center" mb="6">
              <Text mr="6px" minWidth="120px">
                Ledger Name:
              </Text>
              <Input
                ml="auto"
                width="300px"
                placeholder="Enter ledger name"
                value={lineItemData.ledgerName}
                onChange={(e) => {
                  setLineItemData((state) => {
                    if (!state) return state;
                    return {
                      ...state,
                      ledgerName: e.target.value,
                    };
                  });
                }}
              />
            </Flex>
            <Flex alignItems="center" mb="6">
              <Text mr="6px" minWidth="120px">
                Value:
              </Text>
              <Input
                ml="auto"
                type="number"
                width="300px"
                placeholder="Enter value"
                value={String(lineItemData.value)}
                onChange={(e) => {
                  setLineItemData((state) => {
                    if (!state) return state;
                    return {
                      ...state,
                      value: Number(e.target.value),
                    };
                  });
                }}
              />
            </Flex>
            <Flex justifyContent="space-between">
              <Button
                colorScheme="red"
                variant="outline"
                onClick={async () => {
                  await onDeleteFinStatement();
                  onCancel();
                }}
              >
                Delete Statement
              </Button>

              <Button
                type="button"
                mr="5"
                onClick={async () => {
                  await onConfirm();
                  onCancel();
                }}
              >
                Create
              </Button>
              <Button onClick={() => onCancel()}>Cancel</Button>
            </Flex>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
